import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Button, Alert, Spinner, Modal, Form, Row, Col, InputGroup, Card } from 'react-bootstrap';
import api from '../../api/axios';
import { useNotification } from '../../contexts/NotificationContext';

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    external_id: '',
    notes: ''
  });
  const { notify, confirm } = useNotification();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const filteredCompanies = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) {
      return companies;
    }

    return companies.filter((company) => {
      const haystack = [
        String(company.id),
        company.name,
        company.external_id,
        company.notes
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(value);
    });
  }, [companies, searchTerm]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/api/admin/companies');
      setCompanies(response.data);
      setLoading(false);
      setError('');
    } catch (err) {
      console.error('Fetch companies error:', err);
      setError('Failed to load companies');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      external_id: '',
      notes: ''
    });
    setEditingCompanyId(null);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleShowModal = (company = null) => {
    if (company) {
      setFormData({
        name: company.name,
        external_id: company.external_id || '',
        notes: company.notes || ''
      });
      setEditingCompanyId(company.id);
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSaveCompany = async () => {
    try {
      if (!formData.name.trim()) {
        notify('error', 'Company name is required');
        return;
      }

      setActionLoading(true);

      if (editingCompanyId) {
        await api.put(`/api/admin/companies/${editingCompanyId}`, formData);
        notify('success', 'Company updated successfully');
      } else {
        await api.post('/api/admin/companies', formData);
        notify('success', 'Company created successfully');
      }

      await fetchCompanies();
      handleCloseModal();
      setSuccess('');
    } catch (err) {
      console.error('Save company error:', err);
      notify('error', err.response?.data?.error || 'Failed to save company');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCompany = async (companyId) => {
    const result = await confirm(
      'Are you sure you want to delete this company?',
      'This action cannot be undone.'
    );

    if (!result) {
      return;
    }

    try {
      setActionLoading(true);
      await api.delete(`/api/admin/companies/${companyId}`);
      notify('success', 'Company deleted successfully');
      await fetchCompanies();
    } catch (err) {
      console.error('Delete company error:', err);
      notify('error', err.response?.data?.error || 'Failed to delete company');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/api/admin/companies/export/xlsx', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'companies-export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      notify('success', 'Companies exported successfully');
    } catch (err) {
      console.error('Export error:', err);
      notify('error', 'Failed to export companies');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/api/admin/companies/import-template', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'company-import-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      notify('success', 'Template downloaded successfully');
    } catch (err) {
      console.error('Download template error:', err);
      notify('error', 'Failed to download template');
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleImportCompanies = async () => {
    if (!importFile) {
      notify('error', 'Please select a file');
      return;
    }

    try {
      setImportLoading(true);

      const formDataObj = new FormData();
      formDataObj.append('file', importFile);

      const response = await api.post('/api/admin/companies/import', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data);
      notify('success', 'Companies imported successfully');
      await fetchCompanies();
    } catch (err) {
      console.error('Import error:', err);
      notify('error', err.response?.data?.error || 'Failed to import companies');
    } finally {
      setImportLoading(false);
    }
  };

  const handleSyncCustomers = async () => {
    try {
      setSyncLoading(true);
      const response = await api.post('/api/admin/companies/sync-customers');
      const syncResult = response.data?.result || {};
      notify(
        'success',
        `Customer sync completed. Created ${syncResult.createdCompanies || 0}, updated ${syncResult.updatedCompanies || 0}.`
      );
      await fetchCompanies();
    } catch (err) {
      console.error('Sync customers error:', err);
      notify('error', err.response?.data?.error || 'Failed to sync customers from Business Central');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResults(null);
  };

  if (loading) {
    return (
      <Container className="py-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1>Companies Management</h1>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>🔍</InputGroup.Text>
                <Form.Control
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="text-end">
              <Button
                variant="outline-dark"
                onClick={handleSyncCustomers}
                className="me-2"
                disabled={syncLoading}
              >
                {syncLoading ? 'Syncing...' : '🔄 Sync BC Customers'}
              </Button>
              <Button
                variant="primary"
                onClick={() => handleShowModal()}
                className="me-2"
              >
                ➕ Add Company
              </Button>
              <Button
                variant="success"
                onClick={handleExport}
                className="me-2"
              >
                ⬇️ Export
              </Button>
              <Button
                variant="info"
                onClick={() => setShowImportModal(true)}
              >
                ⬆️ Import
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {filteredCompanies.length === 0 ? (
        <Alert variant="info">No companies found</Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>External ID</th>
              <th>Notes</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCompanies.map((company) => (
              <tr key={company.id}>
                <td>{company.id}</td>
                <td>{company.name}</td>
                <td>{company.external_id || '-'}</td>
                <td>{company.notes ? company.notes.substring(0, 50) + (company.notes.length > 50 ? '...' : '') : '-'}</td>
                <td>{new Date(company.created_at).toLocaleDateString()}</td>
                <td>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => handleShowModal(company)}
                    className="me-2"
                    disabled={actionLoading}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDeleteCompany(company.id)}
                    disabled={actionLoading}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCompanyId ? 'Edit Company' : 'Create New Company'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Company Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                placeholder="Enter company name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>External ID</Form.Label>
              <Form.Control
                type="text"
                name="external_id"
                value={formData.external_id}
                onChange={handleFormChange}
                placeholder="Optional external ID"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                name="notes"
                value={formData.notes}
                onChange={handleFormChange}
                placeholder="Optional notes"
                rows={3}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseModal}
            disabled={actionLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveCompany}
            disabled={actionLoading}
          >
            {actionLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Modal */}
      <Modal show={showImportModal} onHide={handleCloseImportModal} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Import Companies</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Select File (XLSX)</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx"
              onChange={handleImportFile}
              disabled={importLoading}
            />
          </Form.Group>

          <Button
            variant="info"
            onClick={handleDownloadTemplate}
            className="mb-3"
            block
            disabled={importLoading}
          >
            📥 Download Import Template
          </Button>

          {importResults && (
            <Alert variant="info">
              <div>Created: {importResults.created}</div>
              <div>Updated: {importResults.updated}</div>
              {importResults.errors && importResults.errors.length > 0 && (
                <div className="mt-2">
                  <strong>Errors:</strong>
                  <ul className="mt-2 mb-0">
                    {importResults.errors.map((error, idx) => (
                      <li key={idx}>
                        Row {error.row}: {error.error} ({error.company})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseImportModal}
            disabled={importLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleImportCompanies}
            disabled={importLoading || !importFile}
          >
            {importLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Importing...
              </>
            ) : (
              'Import'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminCompanies;
